import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { countryName } from "@/lib/utils_gg";

export const fmtSetDate = (d, lang) => new Date(d + "T00:00:00").toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

export function SetlistTracks({ tracks, testid = "setlist-tracks" }) {
  return (
    <ol className="divide-y divide-foreground/15" data-testid={testid}>
      {tracks.map((tr, i) => (
        <li key={i} className="py-1.5 grid grid-cols-[28px_1fr] gap-2 text-sm leading-snug" data-testid={`setlist-track-${i}`}>
          <span className="font-display text-primary tabular-nums">{String(i + 1).padStart(2, "0")}</span>
          <span><span className="font-medium">{tr.artist}</span><span className="text-muted-foreground"> — </span>{tr.title}</span>
        </li>
      ))}
    </ol>
  );
}

export function SetlistToggle({ open, onClick, testid }) {
  const { t } = useApp();
  return <button type="button" onClick={onClick} className="eyebrow !text-[10px] text-primary hover:underline whitespace-nowrap" data-testid={testid}>{open ? `${t("closeSetlist")} ↑` : `${t("setlist")} →`}</button>;
}

export function SetlistPanel({ title, tracks, onClose, testid = "setlist-panel", actions }) {
  const { t } = useApp();
  return (
    <div className="mt-2 mb-1 border-l-2 border-primary pl-3 sm:pl-4 py-1" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-display uppercase text-sm tracking-wide">{title}</div>
        {actions}
      </div>
      <SetlistTracks tracks={tracks} />
      <button type="button" onClick={onClose} className="mt-2 eyebrow !text-[10px] text-primary hover:underline" data-testid={`${testid}-close`}>{t("closeSetlist")} ↑</button>
    </div>
  );
}

let _trackSeq = 0;
const empty = () => ({ artist: "", title: "", _k: `t${++_trackSeq}` });
const withKeys = (list) => list.map(x => ({ ...x, _k: x._k || `t${++_trackSeq}` }));

export function SetlistEditor({ event, djUserId, initial, onClose, onSaved, onDeleted }) {
  const { t, lang } = useApp();
  const [tracks, setTracks] = useState(initial?.length ? withKeys(initial) : [empty(), empty(), empty()]);
  const [drag, setDrag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const upd = (i, k, v) => setTracks(ts => ts.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const move = (from, to) => { if (to < 0 || to >= tracks.length) return; setTracks(ts => { const n = [...ts]; const [x] = n.splice(from, 1); n.splice(to, 0, x); return n; }); };
  const remove = i => setTracks(ts => ts.filter((_, j) => j !== i));
  const save = async () => {
    const clean = tracks.map(x => ({ artist: x.artist.trim(), title: x.title.trim() })).filter(x => x.artist || x.title);
    if (!clean.length) { toast.error(t("setlistEmpty")); return; }
    if (clean.some(x => !x.artist || !x.title)) { toast.error(t("setlistIncomplete")); return; }
    setSaving(true);
    try { const { data } = await api.put(`/events/${event.event_id}/setlists/${djUserId}`, { tracks: clean }); toast.success(t("setlistSaved")); onSaved(data); onClose(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    try { await api.delete(`/events/${event.event_id}/setlists/${djUserId}`); toast.success(t("setlistDeleted")); onDeleted?.(); onClose(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-foreground/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="setlist-editor">
      <div className="bg-background border border-foreground w-full sm:max-w-xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-foreground p-4">
          <div className="min-w-0"><div className="eyebrow text-primary">{t("setlist")}</div>
            <h2 className="font-display uppercase text-2xl leading-none mt-1 truncate">{event.name}</h2>
            <div className="text-xs text-muted-foreground mt-1">{fmtSetDate(event.start_date, lang)} · {event.city}</div></div>
          <button onClick={onClose} className="gg-icon-btn" data-testid="setlist-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-[20px_28px_1fr_1fr_auto] gap-2 eyebrow !text-[10px] text-muted-foreground mb-1"><span /><span>#</span><span>{t("artist")}</span><span>{t("trackTitle")}</span><span /></div>
          <ul className="space-y-1.5" data-testid="setlist-editor-rows">
            {tracks.map((tr, i) => (
              <li key={tr._k} draggable onDragStart={() => setDrag(i)} onDragOver={e => e.preventDefault()} onDrop={() => { if (drag !== null && drag !== i) move(drag, i); setDrag(null); }} onDragEnd={() => setDrag(null)}
                className={`grid grid-cols-[20px_28px_1fr_1fr_auto] gap-2 items-center ${drag === i ? "opacity-40" : ""}`} data-testid={`setlist-row-${i}`}>
                <span className="cursor-grab text-muted-foreground hidden sm:inline-flex" title={t("dragToReorder")}><GripVertical className="w-4 h-4" /></span>
                <span className="flex flex-col sm:hidden"><button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="disabled:opacity-20" data-testid={`setlist-up-${i}`}><ChevronUp className="w-4 h-4" /></button><button type="button" onClick={() => move(i, i + 1)} disabled={i === tracks.length - 1} className="disabled:opacity-20" data-testid={`setlist-down-${i}`}><ChevronDown className="w-4 h-4" /></button></span>
                <span className="font-display text-primary tabular-nums text-sm">{String(i + 1).padStart(2, "0")}</span>
                <input className="gg-input !py-1.5 !px-2 text-sm" placeholder={t("artist")} value={tr.artist} onChange={e => upd(i, "artist", e.target.value)} data-testid={`setlist-artist-${i}`} />
                <input className="gg-input !py-1.5 !px-2 text-sm" placeholder={t("trackTitle")} value={tr.title} onChange={e => upd(i, "title", e.target.value)} data-testid={`setlist-title-${i}`} />
                <button type="button" onClick={() => remove(i)} className="gg-icon-btn !p-1 text-muted-foreground hover:text-[#A84A3F]" data-testid={`setlist-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => setTracks(ts => [...ts, empty()])} className="mt-3 gg-btn !py-1.5 !px-2.5 text-[10px]" data-testid="setlist-add-track"><Plus className="w-3.5 h-3.5" />{t("addTrack")}</button>
          <p className="mt-3 text-xs text-muted-foreground">{t("setlistReorderHint")}</p>
        </div>
        <div className="border-t border-foreground p-4 flex flex-wrap items-center gap-2">
          {initial?.length > 0 && (confirmDel
            ? <span className="text-xs flex items-center gap-2" data-testid="setlist-delete-confirm">{t("deleteSetlistQ")} <button onClick={del} className="gg-btn gg-status-cancelled !border-transparent !py-1 !px-2 text-[10px]" data-testid="setlist-delete-ok">{t("delete")}</button><button onClick={() => setConfirmDel(false)} className="gg-btn !py-1 !px-2 text-[10px]">{t("cancel")}</button></span>
            : <button onClick={() => setConfirmDel(true)} className="gg-btn !py-2 !px-3 text-xs text-[#A84A3F] border-[#A84A3F]" data-testid="setlist-delete"><Trash2 className="w-3.5 h-3.5" />{t("deleteSetlist")}</button>)}
          <span className="ml-auto flex gap-2">
            <button onClick={onClose} className="gg-btn !py-2 !px-3 text-xs" data-testid="setlist-cancel">{t("cancel")}</button>
            <button onClick={save} disabled={saving} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid="setlist-save">{t("saveSetlist")}</button>
          </span>
        </div>
      </div>
    </div>
  );
}

// SETLISTS section on a DJ profile. Owner (and admins) get EDIT per row + a subtle "+ ADD SETLIST" picker.
export function ProfileSetlists({ userId }) {
  const { t, lang, user } = useApp();
  const [items, setItems] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [open, setOpen] = useState(null);
  const [edit, setEdit] = useState(null);
  const [picking, setPicking] = useState(false);
  const isOwner = user?.user_id === userId;
  const canEdit = isOwner || user?.role === "admin";
  const load = () => {
    api.get(`/users/${userId}/setlists`).then(r => setItems(r.data.items)).catch(() => {});
    if (isOwner) api.get("/my/setlist-eligible").then(r => setEligible(r.data.items.filter(e => !e.has_setlist))).catch(() => {});
  };
  useEffect(() => { load(); }, [userId, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length && !isOwner) return null;
  const EditBtn = ({ s }) => <button onClick={() => setEdit({ event: s.event, tracks: s.tracks })} className="eyebrow !text-[10px] hover:text-primary inline-flex items-center gap-1 whitespace-nowrap" data-testid={`setlist-edit-${s.event_id}`}><Pencil className="w-3 h-3" />{t("editSetlist")}</button>;
  return (
    <div className="mt-8 border-t border-foreground pt-5" data-testid="user-profile-setlists">
      <div className="eyebrow mb-1">{t("setlists")}</div>
      <div>{items.map(s => {
        const e = s.event; const isOpen = open === s.setlist_id;
        return (
          <div key={s.setlist_id} className="border-b border-foreground/30" data-testid={`setlist-entry-${s.event_id}`}>
            <div className="flex items-center gap-3 py-2.5">
              <span className="font-display text-xs w-24 shrink-0 tracking-wide">{fmtSetDate(e.start_date, lang)}</span>
              <Link to={`/events/${e.slug}`} className="font-display text-base leading-tight truncate flex-1 hover:text-primary" data-testid={`setlist-event-link-${s.event_id}`}>{e.name}</Link>
              <span className="text-xs text-muted-foreground hidden sm:inline shrink-0">{e.city}, {countryName(e.country, lang)}</span>
              {canEdit && <span className="hidden sm:inline"><EditBtn s={s} /></span>}
              <SetlistToggle open={isOpen} onClick={() => setOpen(isOpen ? null : s.setlist_id)} testid={`setlist-toggle-${s.event_id}`} />
            </div>
            {isOpen && <div className="pb-3"><SetlistPanel title={e.name} tracks={s.tracks} onClose={() => setOpen(null)} testid={`setlist-panel-${s.event_id}`}
              actions={canEdit && <span className="sm:hidden"><EditBtn s={s} /></span>} /></div>}
          </div>
        );
      })}</div>
      {!items.length && isOwner && <p className="text-sm text-muted-foreground py-2" data-testid="setlists-empty-own">{t("noSetlistsOwn")}</p>}
      {isOwner && (
        <div className="mt-3" data-testid="add-setlist-block">
          {!picking
            ? <button onClick={() => setPicking(true)} className="eyebrow !text-[10px] text-primary hover:underline inline-flex items-center gap-1" data-testid="add-setlist"><Plus className="w-3 h-3" />{t("addSetlist")}</button>
            : (
              <div className="border border-foreground p-3" data-testid="add-setlist-picker">
                <div className="flex items-center justify-between gap-2 mb-1"><span className="eyebrow !text-[10px]">{t("pickEventForSetlist")}</span><button onClick={() => setPicking(false)} className="gg-icon-btn !p-0.5" data-testid="add-setlist-picker-close"><X className="w-3.5 h-3.5" /></button></div>
                {!eligible.length && <p className="text-sm text-muted-foreground" data-testid="add-setlist-empty">{t("noEligibleEvents")}</p>}
                {eligible.map(e => (
                  <button key={e.event_id} onClick={() => { setPicking(false); setEdit({ event: e, tracks: [] }); }} className="w-full text-left flex items-center gap-3 py-2 border-b border-foreground/20 last:border-0 hover:text-primary" data-testid={`add-setlist-${e.event_id}`}>
                    <span className="font-display text-xs w-24 shrink-0 tracking-wide">{fmtSetDate(e.start_date, lang)}</span>
                    <span className="font-display text-base leading-tight truncate flex-1">{e.name}</span>
                    <span className="text-xs text-muted-foreground hidden sm:inline shrink-0">{e.city}</span>
                    <span className="eyebrow !text-[10px] text-primary whitespace-nowrap">{t("addSetlist")} →</span>
                  </button>
                ))}
              </div>
            )}
        </div>
      )}
      {edit && <SetlistEditor event={edit.event} djUserId={userId} initial={edit.tracks} onClose={() => setEdit(null)} onSaved={load} onDeleted={() => { setOpen(null); load(); }} />}
    </div>
  );
}
